import type { ProductivityRange } from '@/plugins/store/actions/queries/active-groups';

export type ProductivityRangeOption = 'productionGroup' | '3m' | '6m' | '9m' | '12m';

export const RANGE_OPTIONS: ProductivityRangeOption[] = ['productionGroup', '3m', '6m', '9m', '12m'];

const MONTHS_BY_OPTION: Record<Exclude<ProductivityRangeOption, 'productionGroup'>, number> = {
    '3m': 3,
    '6m': 6,
    '9m': 9,
    '12m': 12,
};

// A calendar date the request states, resolved in UTC.
//
// Both halves have to be UTC or the pair is not reproducible. `toISOString().slice(0, 10)` already
// took the UTC date of an instant, which is viewer-independent - but the arithmetic in front of it
// was not: date-fns' `subMonths` walks the *viewer's* calendar, so two people pressing "3 months" on
// the same farm at the same moment could send ranges a day apart, giving them different Vue Query
// cache keys, a different first bucket and a shifted "Dag N". Stepping the UTC month directly makes
// the answer the same for both.
//
// This is the request boundary only. The axis's own "now" comes from the response's
// `directory_local_now`, and the server clamps `to` to the farm's own today, so a viewer whose UTC
// date is a day ahead of the farm's simply asks for a day the server trims.
function monthsBackUtc(now: Date, months: number): string {
    const day = now.getUTCDate();
    const target = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - months, 1));
    // Clamp into the target month rather than letting Date roll over: three months back from 31 May
    // is the last day of February, not the 2nd or 3rd of March.
    const lastDayOfTarget = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
    target.setUTCDate(Math.min(day, lastDayOfTarget));
    return target.toISOString().slice(0, 10);
}

function todayUtc(now: Date): string {
    return now.toISOString().slice(0, 10);
}

// Resolves a range button to an explicit {from, to}. "Production Group" falls back to 3 months back
// when the Section has none running, the same fallback the chart's default range already uses -
// otherwise picking it for an empty Section would ask for an empty range.
export function resolveRangeOption(option: ProductivityRangeOption, productionGroupStart: string | null, now: Date): ProductivityRange {
    const to = todayUtc(now);
    if (option === 'productionGroup') {
        return { from: productionGroupStart ?? monthsBackUtc(now, 3), to };
    }
    return { from: monthsBackUtc(now, MONTHS_BY_OPTION[option]), to };
}
